import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faTable } from "@fortawesome/free-solid-svg-icons";
import apiService from "../../services/apiService";
import { useToast } from "../../contexts/ToastContext";
import "./styles/AddToTableModal.css";

const TOOL_OPTIONS = [
  { id: "decipher", name: "Decipher", hint: "Format: q1r1c1 (grids), q1r1 (rows only)" },
  { id: "sawtooth", name: "Sawtooth", hint: "Keep original format" },
  { id: "confirmit", name: "Confirmit", hint: "Keep original format" },
  { id: "spss", name: "SPSS Dimensions", hint: "Format: Grid_q1_1_1 (grids), Grid_q1_1 (rows only)" },
  { id: "qualtrics", name: "Qualtrics", hint: "Keep original format" },
  { id: "pollstudio", name: "Poll Studio", hint: "Keep original format" },
  { id: "intelli", name: "Intelli Survey", hint: "Keep original format" },
  { id: "none", name: "Keep as is (no changes)", hint: "No variable name changes" },
];

const AddToTableModal = ({ isOpen, onClose, projectId, sdataGroup, onSuccess }) => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  const handleToolSelect = async (toolId) => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${apiService.baseURL}/transform-qgroup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          projectId,
          qgroupData: sdataGroup,
          toolUsed: toolId,
        }),
      });

      const result = await response.json();
      if (result.success) {
        onSuccess(result.data);
        onClose();
      } else {
        throw new Error(result.error || "Transform failed");
      }
    } catch (err) {
      toast.error("Error: " + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackdropClick = () => {
    if (!isProcessing) onClose();
  };

  return (
    <>
      <div className="atm-backdrop" onClick={handleBackdropClick}>
        <div className="atm-panel" onClick={(e) => e.stopPropagation()}>
          <div className="atm-modal-header">
            <div className="atm-header-left">
              <h3 className="atm-title">
                <FontAwesomeIcon icon={faTable} style={{ marginRight: "8px" }} />
                Select Tool for Variable Name Formatting
              </h3>
            </div>
            <div className="atm-header-right">
              <button className="atm-close-btn" onClick={onClose} disabled={isProcessing} title="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          <div className="atm-panel-body">
          <p className="atm-subtitle">Choose a tool to format variable names accordingly:</p>

          <div className="atm-grid">
            {TOOL_OPTIONS.map((tool) => (
              <button
                key={tool.id}
                className={`atm-tool-btn ${tool.id === "none" ? "atm-tool-btn--neutral" : "atm-tool-btn--primary"}`}
                onClick={() => handleToolSelect(tool.id)}
                disabled={isProcessing}
                title={tool.hint}
              >
                {tool.name}
              </button>
            ))}
          </div>

          <div className="atm-legend">
            <strong>Formatting rules:</strong>
            <ul>
              <li><strong>Decipher:</strong> q1r1c1 (grids), q1r1 (rows only)</li>
              <li><strong>SPSS Dimensions:</strong> Grid_q1_1_1 (grids), Grid_q1_1 (rows only)</li>
              <li><strong>Other tools:</strong> Keep original format</li>
            </ul>
          </div>

          {isProcessing && <div className="atm-processing">Processing…</div>}
          </div>
        </div>
      </div>

      {isProcessing && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10001,
          }}
        >
          <div className="export-loader" />
          <p
            style={{
              color: "white",
              marginTop: "20px",
              fontSize: "18px",
              fontWeight: "600",
            }}
          >
            Adding Data Table...
          </p>
        </div>
      )}
    </>
  );
};

export default AddToTableModal;
